// pages/Community/SkoolProspectAnalytics.jsx
//
// The Skool prospect funnel on the Community Analytics tab: where everyone
// stands now (worker services/prospectClassifier), how many moved INTO each
// state inside the page's window, and the calls that are booked. Reads the same
// /api/connectors/skool/prospects the board uses — no separate aggregate, so
// the two can never disagree.
import React, { useEffect, useState, useMemo } from "react";
import { getSkoolProspects } from "api/connectors/connectors";
import { COLUMNS } from "./SkoolProspects";

const WARM = ["close_to_converting", "booked", "converted"];
const fmt = (d) => { try { return new Date(d).toLocaleString(); } catch (_) { return "—"; } };
const nameOf = (p) => (p.name && !/^[a-z0-9-]+$/.test(p.name) ? p.name : (p.handle || p.name || p.userId));

/** Pure: the numbers the card shows. Exported for tests. */
export function summarize(prospects, days, now = Date.now()) {
  const since = now - days * 864e5;
  const counts = Object.fromEntries(COLUMNS.map((c) => [c.key, 0]));
  const movedIn = Object.fromEntries(COLUMNS.map((c) => [c.key, 0]));
  let manual = 0;
  for (const p of prospects || []) {
    if (counts[p.state] != null) counts[p.state] += 1;
    if (p.stateSource === "manual") manual += 1;
    // Each change INTO a state inside the window. A person can count in two
    // columns if they moved twice — that is movement, not headcount.
    for (const h of p.history || []) {
      if (h && h.at && new Date(h.at).getTime() >= since && movedIn[h.state] != null) movedIn[h.state] += 1;
    }
  }
  const total = (prospects || []).length;
  const warm = WARM.reduce((a, k) => a + counts[k], 0);
  const upcoming = (prospects || [])
    .filter((p) => p.state === "booked" && p.booking && p.booking.startsAt && new Date(p.booking.startsAt).getTime() > now - 3 * 36e5)
    .sort((a, b) => new Date(a.booking.startsAt) - new Date(b.booking.startsAt));
  return { total, counts, movedIn, warm, warmPct: total ? Math.round((warm / total) * 100) : 0, manual, upcoming };
}

export default function SkoolProspectAnalytics({ days = 30 }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    getSkoolProspects({})
      .then((r) => { if (live) setRows((r && r.prospects) || []); })
      .catch((e) => { if (live) { setErr((e && (e.message || e.error)) || "Could not load"); setRows([]); } });
    return () => { live = false; };
  }, []);

  const s = useMemo(() => summarize(rows || [], days), [rows, days]);

  return (
    <div className="card border-0 mb-3" data-testid="skool-prospect-analytics">
      <div className="card-body">
        <div className="d-flex align-items-baseline justify-content-between mb-2">
          <h6 className="m-0" style={{ fontWeight: 700 }}>Skool prospects</h6>
          <span className="text-muted small">
            {rows == null ? "Loading…" : `${s.total} people · ${s.warmPct}% warm (close / booked / converted) · ${s.manual} set by you`}
          </span>
        </div>
        {err && <div className="text-muted small mb-2">{err}</div>}

        <div className="d-flex" style={{ gap: 10, flexWrap: "wrap" }}>
          {COLUMNS.map((c) => (
            <div key={c.key} data-testid={`skool-kpi-${c.key}`} title={c.hint}
              style={{ flex: "1 1 130px", minWidth: 130, background: c.bg, border: `1px solid ${c.color}33`, borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontSize: 12, color: c.color, fontWeight: 700 }}>{c.label}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>{s.counts[c.key]}</div>
              <div style={{ fontSize: 11, color: "#64748b" }}>+{s.movedIn[c.key]} moved in · {days}d</div>
            </div>
          ))}
        </div>

        {/* Funnel bar: the same counts as proportions, so the shape reads at a glance. */}
        {s.total > 0 && (
          <div style={{ display: "flex", height: 10, borderRadius: 6, overflow: "hidden", marginTop: 12 }} aria-label="Prospect funnel">
            {COLUMNS.filter((c) => s.counts[c.key]).map((c) => (
              <div key={c.key} title={`${c.label}: ${s.counts[c.key]}`} style={{ width: `${(s.counts[c.key] / s.total) * 100}%`, background: c.color }} />
            ))}
          </div>
        )}

        <div style={{ marginTop: 14 }}>
          <div className="small font-weight-bold mb-1">Booked calls</div>
          {!s.upcoming.length && <div className="text-muted small">No upcoming calls.</div>}
          {s.upcoming.map((p) => (
            <div key={p._id} data-testid="skool-upcoming-call" className="d-flex small" style={{ gap: 10, padding: "4px 0", borderBottom: "1px solid #f1f5f9" }}>
              <b>{nameOf(p)}</b>
              <span style={{ color: "#6a3fb5" }}>{fmt(p.booking.startsAt)}</span>
              <span className="text-muted">{p.booking.service || ""}</span>
              <span className="text-muted ml-auto">{(p.reminders || []).length}/4 reminders sent</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
